import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { SectionCard } from './SectionCard';

export interface SummaryCardProps {
  summary: string | null;
  canEdit: boolean;
  onSave: (summary: string | null) => Promise<unknown>;
}

/** Markdown summary of the candidate with an inline editor. */
export function SummaryCard({ summary, canEdit, onSave }: SummaryCardProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(summary ?? '');
  const [saving, setSaving] = React.useState(false);
  const id = React.useId();

  React.useEffect(() => {
    if (!editing) setDraft(summary ?? '');
  }, [summary, editing]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim() ? draft : null);
      setEditing(false);
      toast.success('Summary saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the summary');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Summary"
      description="Internal notes in markdown; never copied into documents as-is."
      actions={
        canEdit ? (
          editing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void save()} loading={saving}>
                Save
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil aria-hidden="true" /> Edit
            </Button>
          )
        ) : null
      }
    >
      {editing ? (
        <div className="space-y-1.5">
          <Label htmlFor={id}>Summary (markdown)</Label>
          <Textarea
            id={id}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            autoFocus
            placeholder="Strengths, gaps, interview notes…"
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void save();
            }}
          />
        </div>
      ) : summary ? (
        <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No summary yet.</p>
      )}
    </SectionCard>
  );
}
